import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import Person from '@mui/icons-material/Person';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { getBusinessContacts } from 'lib/api/contacts';
import { Contact } from 'lib/api/contacts/types';
import { submitVerification } from 'lib/api/documentConfirmation';
import CreateContact from 'lib/common/Forms/Contacts/Create';
import ContactsTable from 'lib/common/Tables/ContactsTable';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { TextArea } from 'lib/designSystem/TextArea';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import Uploader from 'lib/designSystem/Uploader';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

const availableTypes = [
  { id: 'mail', name: 'Correo' },
  { id: 'phone', name: 'Teléfono' },
  { id: 'whatsapp', name: 'Whatsapp' },
  { id: 'visit', name: 'Visita' },
  { id: 'postVerified', name: 'Post verificado' },
];

const allowedFileTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
];

const maxFileSize = 10 * 1024 * 1024; // 10MB

const ConfirmationForm = (props: any) => {
  const {
    status,
    setStatus,
    type,
    setType,
    message,
    setMessage,
    selectedDocuments,
    handleFileChange,
    uploadedFileName,
  } = props;

  const confirmTypesInDocuments = selectedDocuments.map(
    (document: any) => document.confirmType,
  );
  const availableTypeForThisDocuments = availableTypes.filter(
    (type: any) => !confirmTypesInDocuments.includes(type.name),
  );

  return (
    <div>
      <Grid container spacing={2} paddingRight={3}>
        <Grid item xs={3}>
          <Stack direction={'column'} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="status-label">{'Status'}</InputLabel>
              <Select
                labelId="status-label"
                id="Filtrar por status"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label={'Status'}
              >
                <MenuItem key={'VERIFIED'} value={'VERIFIED'}>
                  Verificada
                </MenuItem>
                <MenuItem key={'REJECTED'} value={'REJECTED'}>
                  Rechazada
                </MenuItem>
                <MenuItem key={'PENDING'} value={'PENDING'}>
                  Pendiente
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="filter-label">{'Tipo'}</InputLabel>
              <Select
                labelId="filter-label"
                id="Filtrar por estado"
                fullWidth
                value={type}
                onChange={(e) => setType(e.target.value)}
                label={'Tipo'}
              >
                {availableTypeForThisDocuments.map((x: any, index: any) => (
                  <MenuItem key={index} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Uploader
              handleFileChange={handleFileChange}
              buttonText="Subir PDF / Imagen"
              uploadedFileName={uploadedFileName}
              accept={allowedFileTypes.join(',')}
            />
          </Stack>
        </Grid>
        <Grid item xs={9}>
          <TextArea
            minRows={8}
            maxRows={12}
            value={message}
            placeholder="Comentario"
            onChange={(e) => setMessage(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const ConfirmationModule = (props: any) => {
  const dispatch = useDispatch();
  const {
    documents,
    stakeholderName,
    stakeholderIdentifier,
    setUp,
    open,
    setOpen,
    pagination,
  } = props;
  const { data, isPending } = getBusinessContacts(
    dispatch,
    stakeholderIdentifier,
  );

  const [openModal, setOpenModal] = useState(false);
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('');
  const [message, setMessage] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState<any>(null);

  const contacts = data?.items ?? [];

  const handleFileChange = (event: any) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile && allowedFileTypes.includes(uploadedFile.type)) {
      if (uploadedFile.size > maxFileSize) {
        showAlert(
          dispatch,
          'error',
          'El archivo excede el tamaño máximo permitido (10MB)',
        );
      } else {
        setUploadedFileName(uploadedFile.name);
        setFile(uploadedFile);
      }
    } else {
      showAlert(
        dispatch,
        'error',
        'Por favor, sube un archivo tipo .pdf, .png, .jpg o .jpeg',
      );
    }
  };

  const validateConfirmation = () => {
    if (type === '') {
      showAlert(dispatch, 'error', 'Debes seleccionar un tipo de confirmación');
      return false;
    }
    if (message === '') {
      showAlert(dispatch, 'error', 'Debes agregar un comentario');
      return false;
    }
    if (!status) {
      showAlert(dispatch, 'error', 'Debes seleccionar un status');
      return false;
    }
    if (!contact && type !== 'postVerified') {
      showAlert(dispatch, 'error', 'Debes seleccionar un contacto');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!validateConfirmation()) {
      setLoading(false);
      return;
    }
    try {
      const data = new FormData();
      data.append('type', type);
      data.append('message', message);
      if (contact) {
        data.append('contactId', contact.id.toString());
      }
      data.append('documents', JSON.stringify(documents));
      data.append('status', status);
      if (file) {
        data.append('file', file);
      }
      await submitVerification(dispatch, {
        data,
      });
      setLoading(false);
      await setUp({ page: 0, pageSize: pagination.pageSize });
      showAlert(dispatch, 'success', 'Facturas confirmadas correctamente');
      setOpen(false);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al confirmar facturas');
    }
    setLoading(false);
  };

  const handleShowBusinessDetails = (id: string) => {
    const row: any = documents.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: string) => {
    const row: any = documents.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier,
        name: row.stakeholderName,
      }),
    );
  };

  const handleSelectContact = (contactId: number) => {
    const contact = contacts.find((x: Contact) => x.id === contactId);
    if (!contact) return;
    setContact(contact);
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Nuevo contacto',
      action: () => setOpenModal(true),
      color: 'primary',
    },
  ];

  return (
    <>
      <Dialog maxWidth="xl" open={open} onClose={() => setOpen(false)}>
        {loading || (isPending && <CenteredCircularProgress type="dialog" />)}
        {!loading && (
          <>
            <DialogContent>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" padding="16px 0px" fontWeight={600}>
                  Verificar documentos del deudor {stakeholderName} (
                  {stakeholderIdentifier})
                </Typography>
                <IconButton
                  onClick={() => handleShowStakeholderDetails(documents[0].id)}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </div>
              <div style={{ padding: '10px 0px' }}>
                <EnhancedTable
                  data={documents}
                  dataHeaders={[
                    {
                      key: 'factoringRequestId',
                      label: 'Solicitud',
                      type: 'numeric-id',
                    },
                    { key: 'folio', label: 'Folio' },
                    {
                      key: 'businessName',
                      label: 'Cliente',
                    },
                    {
                      key: 'businessIdentifier',
                      label: 'Rut cliente',
                      handleAction: handleShowBusinessDetails,
                      icon: <InfoIcon fontSize="small" />,
                    },
                    {
                      key: 'amount',
                      label: 'Monto',
                      type: 'money',
                    },
                    {
                      key: 'factoringRequestStatus',
                      label: 'Estado Solicitud',
                      type: 'status',
                    },
                    {
                      key: 'factoringRequestCreationDate',
                      label: 'Fecha Solicitud',
                      type: 'date',
                    },
                    {
                      key: 'confirmDateSII',
                      label: 'Confirmada SII',
                      type: 'date-colored-ok',
                    },
                    {
                      key: 'confirmDate',
                      label: 'Verificación',
                      canNull: true,
                    },
                  ]}
                  rowsPerPageDefault={5}
                  exportToFile="xlsx"
                />
              </div>
              <ConfirmationForm
                status={status}
                setStatus={setStatus}
                type={type}
                setType={setType}
                meesage={message}
                setMessage={setMessage}
                selectedDocuments={documents}
                handleFileChange={handleFileChange}
                uploadedFileName={uploadedFileName}
              />
              {type !== 'postVerified' && (
                <Stack direction="column" spacing={2} mt={2}>
                  <Divider />
                  <HeaderWithActions
                    type="subHeader"
                    variant="body1"
                    buttons={!contact ? buttons : []}
                    title={
                      contact
                        ? 'Contacto seleccionado para la verificación'
                        : 'Selecciona un contacto para verificar los documentos.'
                    }
                  />
                  {contact ? (
                    <Card variant="outlined" sx={{ maxWidth: 480 }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {contact.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {contact.email}
                              {contact.phone && ` · ${contact.phone}`}
                            </Typography>
                            {contact.role && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {contact.role}
                              </Typography>
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setContact(null)}
                            aria-label="Deseleccionar contacto"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : (
                    <ContactsTable
                      data={contacts}
                      showActions={false}
                      showProfiles={false}
                      showObservation={false}
                      showCountry={false}
                      showProfession={false}
                      showMaritalStatus={false}
                      showBusiness={false}
                      handleClick={handleSelectContact}
                    />
                  )}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <CustomButton color="secondary" onClick={() => setOpen(false)}>
                Salir
              </CustomButton>
              <CustomButton color="primary" onClick={() => handleSubmit()}>
                Confirmar
              </CustomButton>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          title="Agregar contacto"
          icon={<Person color="primary" />}
          onClose={() => setOpenModal(false)}
          color="primary"
        />
        <DialogContent>
          <CreateContact
            businessIdentifier={stakeholderIdentifier}
            onCreated={() => setOpenModal(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConfirmationModule;
